import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { toast } from 'sonner';

const QualityControlWorkflow = ({ batches, onRefresh }) => {
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [defectReport, setDefectReport] = useState('');
  const [filterCase, setFilterCase] = useState('all');
  const [uniqueCases, setUniqueCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch unique cases
  useEffect(() => {
    const fetchCases = async () => {
      if (!batches || batches.length === 0) {
        setUniqueCases([]);
        return;
      }

      try {
        setLoadingCases(true);
        
        const caseIds = [...new Set(batches.map(batch => batch?.case_id).filter(Boolean))];
        
        if (caseIds.length === 0) {
          setUniqueCases([]);
          setLoadingCases(false);
          return;
        }

        const { data: casesData, error } = await supabase
          .from('cases')
          .select('id, case_number, patient:patients(id, name)')
          .in('id', caseIds)
          .order('case_number', { ascending: false });

        if (error) throw error;

        const casesMap = casesData?.map(caseItem => ({
          id: caseItem.id,
          caseNumber: caseItem.case_number,
          patientName: caseItem.patient?.name || 'Patient inconnu',
          batchCount: batches.filter(b => b.case_id === caseItem.id && b.status === 'quality_check').length
        })) || [];

        setUniqueCases(casesMap);
      } catch (error) {
        console.error('Error fetching cases:', error);
        setUniqueCases([]);
      } finally {
        setLoadingCases(false);
      }
    };

    fetchCases();
  }, [batches]);

  // Get case info for a batch
  const getCaseInfo = (batch) => {
    if (!batch?.case_id) return null;
    return uniqueCases.find(c => c.id === batch.case_id);
  };

  // Filter batches by quality_check status and case
  const qualityCheckBatches = batches?.filter(b => {
    const statusMatch = b?.status === 'quality_check';
    const caseMatch = filterCase === 'all' || b?.case_id === filterCase;
    return statusMatch && caseMatch;
  }) || [];

  const handleDefectReport = async (batch) => {
    if (!defectReport?.trim()) {
      toast.error('Veuillez saisir un rapport de défaut');
      return;
    }
    
    try {
      setSubmitting(true);

      // Update batch with defect report
      const { error } = await supabase
        .from('production_batches')
        .update({
          quality_status: 'rejected',
          quality_note: defectReport,
          status: 'on_hold', // Change status to on_hold when defect is found
          updated_at: new Date().toISOString()
        })
        .eq('id', batch.id);

      if (error) throw error;

      toast.success(`Défaut signalé pour le lot ${batch.batchNumber || batch.batch_number}`);
      setDefectReport('');
      setSelectedBatch(null);
      
      // Refresh data
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error reporting defect:', error);
      toast.error('Erreur lors du signalement du défaut');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (batch) => {
    try {
      setSubmitting(true);

      // Update batch as approved
      const { error } = await supabase
        .from('production_batches')
        .update({
          quality_status: 'approved',
          quality_note: 'Contrôle qualité approuvé',
          status: 'packaging', // Mark as completed when approved
          completion_date: new Date().toISOString(),
          progress: 100,
          updated_at: new Date().toISOString()
        })
        .eq('id', batch.id);

      if (error) throw error;

      toast.success(`Lot ${batch.batchNumber || batch.batch_number} approuvé`);
      
      // Refresh data
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error approving batch:', error);
      toast.error('Erreur lors de l\'approbation');
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate stats
  const approvedCount = batches?.filter(b => b.quality_status === 'approved').length || 0;
  const rejectedCount = batches?.filter(b => b.quality_status === 'rejected').length || 0;
  const totalQualityChecks = approvedCount + rejectedCount;
  const approvalRate = totalQualityChecks > 0 
    ? Math.round((approvedCount / totalQualityChecks) * 100) 
    : 0;

  return (
    <div className="space-y-6">
      {/* Quality Control Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
              <Icon name="CheckCircle" size={24} className="text-warning" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{qualityCheckBatches?.length}</div>
              <div className="text-sm text-gray-600">En contrôle</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="Check" size={24} className="text-success" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{approvalRate}%</div>
              <div className="text-sm text-gray-600">Taux d'approbation</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
              <Icon name="AlertTriangle" size={24} className="text-error" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{rejectedCount}</div>
              <div className="text-sm text-gray-600">Défauts détectés</div>
            </div>
          </div>
        </div>
      </div>

      {/* Case Filter */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filtrer par cas
        </label>
        {loadingCases ? (
          <div className="text-sm text-gray-500">Chargement des cas...</div>
        ) : (
          <div className="flex items-center gap-4">
            <select
              value={filterCase}
              onChange={(e) => setFilterCase(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">
                Tous les cas ({batches?.filter(b => b.status === 'quality_check').length || 0} lots)
              </option>
              {uniqueCases.filter(c => c.batchCount > 0).map(caseItem => (
                <option key={caseItem.id} value={caseItem.id}>
                  {caseItem.caseNumber} - {caseItem.patientName} ({caseItem.batchCount} lot{caseItem.batchCount > 1 ? 's' : ''})
                </option>
              ))}
            </select>
            
            {filterCase !== 'all' && (
              <button
                onClick={() => setFilterCase('all')}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                <Icon name="X" size={16} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{qualityCheckBatches?.length}</span> lot(s) en contrôle qualité
        </p>
      </div>

      {/* Quality Check Queue */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          File d'attente Contrôle Qualité
        </h3>
        
        {qualityCheckBatches?.length > 0 ? (
          <div className="space-y-4">
            {qualityCheckBatches?.map(batch => {
              const caseInfo = getCaseInfo(batch);
              
              return (
                <div key={batch?.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">
                        {batch?.batchNumber || batch?.batch_number}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {batch?.patientName || batch?.patient_name}
                      </p>
                      
                      {/* Case Info */}
                      {caseInfo && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-50 text-purple-700 text-xs font-medium">
                            <Icon name="FolderOpen" size={12} />
                            {caseInfo.caseNumber}
                          </span>
                        </div>
                      )}
                      
                      {/* Current Phase */}
                      {batch?.current_phase && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium mt-2">
                          <Icon name="Layers" size={12} />
                          Phase {batch.current_phase}
                        </span>
                      )}
                    </div>
                    
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning">
                      En contrôle
                    </span>
                  </div>

                  {/* Existing Quality Note */}
                  {batch?.quality_note && batch?.quality_status && (
                    <div className={`mb-3 p-3 rounded-lg ${
                      batch.quality_status === 'approved' 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon 
                          name={batch.quality_status === 'approved' ? 'Check' : 'AlertTriangle'} 
                          size={16} 
                          className={batch.quality_status === 'approved' ? 'text-green-600' : 'text-red-600'}
                        />
                        <span className={`text-sm font-medium ${
                          batch.quality_status === 'approved' ? 'text-green-900' : 'text-red-900'
                        }`}>
                          {batch.quality_status === 'approved' ? 'Approuvé' : 'Défaut signalé'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{batch.quality_note}</p>
                    </div>
                  )}

                  {/* Action Form */}
                  {selectedBatch?.id === batch?.id ? (
                    <div className="space-y-3">
                      <textarea
                        value={defectReport}
                        onChange={(e) => setDefectReport(e?.target?.value)}
                        placeholder="Décrire le défaut détecté (obligatoire)..."
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="X"
                          onClick={() => {
                            setSelectedBatch(null);
                            setDefectReport('');
                          }}
                          disabled={submitting}
                        >
                          Annuler
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          iconName="AlertTriangle"
                          onClick={() => handleDefectReport(batch)}
                          loading={submitting}
                          disabled={submitting || !defectReport.trim()}
                        >
                          Signaler défaut
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        variant="success"
                        size="sm"
                        iconName="Check"
                        onClick={() => handleApprove(batch)}
                        loading={submitting}
                        disabled={submitting}
                      >
                        Approuver
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="AlertTriangle"
                        onClick={() => {
                          setSelectedBatch(batch);
                          setDefectReport('');
                        }}
                        disabled={submitting}
                      >
                        Signaler défaut
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="CheckCircle" size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-600 mb-2">
              Aucun lot en attente de contrôle qualité
            </p>
            {filterCase !== 'all' && (
              <button
                onClick={() => setFilterCase('all')}
                className="text-sm text-primary hover:underline"
              >
                Voir tous les lots
              </button>
            )}
          </div>
        )}
      </div>

      {/* QR-Linked Documentation */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Documentation liée au QR
        </h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <Icon name="QrCode" size={20} className="text-primary" />
            <span className="text-gray-900">Chaque défaut est lié au QR code du lot</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Icon name="FileText" size={20} className="text-primary" />
            <span className="text-gray-900">
              Traçabilité complète des actions correctives via quality_status et quality_note
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Icon name="Clock" size={20} className="text-primary" />
            <span className="text-gray-900">Horodatage automatique de chaque contrôle</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Icon name="Database" size={20} className="text-primary" />
            <span className="text-gray-900">
              Données enregistrées: quality_status (approved/rejected) et quality_note
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QualityControlWorkflow;